<template>
  <div class="post-container" :style="fontFamilyStyle">
    <Header v-bind="docHeader" />
    <div class="post-content-wrapper">
      <article class="post-article">
        <SiderConsulting :text="detailData?.customer_feedback_text" />
        <Content
          class="max-w-[784px]"
          :content="detailData?.case_describe"
          @current-heading="activeId = $event"
        />
      </article>
    </div>
    <CustomerStories :caseList="list" />

    <footer class="w-full">
      <StartRightNow :retractable="false" :handler="handleTrail" />
      <AppFooter :retractable="false" />
    </footer>
  </div>
</template>

<script setup lang="ts" name="PostDetail">
// 博客详情页和企业案例详情页
import { getAllAdminTags, getCaseDetail, getCaseList } from '~/api';

import { useSeoData } from '~/hooks/useSeo';
import Content from './components/content.vue';
import CustomerStories from './components/customer-stories/index.vue';

import StartRightNow from '~/design/widgets/start-right-now/index.vue';
import Header from './components/header-for-case.vue';
import SiderConsulting from './components/sider-consulting.vue';

const AppFooter = defineAsyncComponent(() =>
  isChinese()
    ? import('~/design/widgets/bottom-footer/footer-cn.vue').then(m => m.default)
    : import('~/design/widgets/bottom-footer/footer-en.vue').then(m => m.default),
);

const seoData = useSeoData();
// 路由相关
const route = useRoute();
const activeId = ref<string>(''); // 当前激活的标题ID
const loading = ref(false);

const is_CN = computed(() => {
  return isChinese();
});

const handleTrail = () => {
  useUserInfo().value
    ? navigateTo('/workspace')
    : navigateTo(
        `/login?redirect=${encodeURIComponent(encodeURIComponent(`${location.origin}/workspace`))}`,
      );
};

const fontFamilyStyle = computed(() => {
  return is_CN.value ? {} : { fontFamily: 'Poppins, sans-serif' };
});

// 使用 useAsyncData 统一管理数据加载
const { data: tagAdminData } = await useAsyncData(
  'tag-admin-list',
  async () => {
    try {
      const res = await getAllAdminTags();
      if (!res.code && res.data) {
        // console.log('tagAdminList loaded:', res.data.tags)
        return res.data;
      }
    } catch (e) {
      console.error('error in tags', e);
    }
    return null;
  },
  {
    server: true,
    lazy: false,
    default: () => null,
  },
);

const { data: detailData } = await useAsyncData(
  `case-detail-${route.params.post}`,
  async () => {
    try {
      loading.value = true;
      const res = await getCaseDetail({
        path_url: route.params?.post as string,
      });
      loading.value = false;

      if (!res.code && res.data) {
        // 处理埋点和 SEO
        trackEvent(TRACK_EVENT.PAGE_ENTERPRISE_CASE_DETAIL, {
          title: res.data?.customer_name,
        });

        seoData.value.push({
          description: res.data.seo_description,
          keywords: res.data.seo_keywords,
          title: res.data.seo_title,
          path: route.path,
        });

        console.log('detail loaded:', res.data);
        return res.data;
      }
    } catch (e) {
      loading.value = false;
      console.error('error loading detail:', e);
    }
    return null;
  },
  {
    server: true,
    lazy: false,
    default: () => null,
  },
);

const { data: listData } = await useAsyncData(
  'case-list',
  async () => {
    try {
      const res = await getCaseList({ page: 1, page_size: 100 });
      if (!res.code && res.data) {
        const filteredList = res.data.results
          .filter(item => item?.path_url !== route.params?.post)
          .map(v => ({
            article_id: v.id,
            cover_url: v.cover_image_url || '',
            name: v.title || '',
            description: v.description || '',
            tag_list: v.tag_list || [],
            customer_name: v.customer_name || '',
            path_url: v.path_url || '',
          }));

        // console.log('list loaded:', filteredList.length, 'items')
        return filteredList;
      }
    } catch (e) {
      console.error('error loading list:', e);
    }
    return [];
  },
  {
    server: true,
    lazy: false,
    default: () => [],
  },
);

// 计算属性：格式化标签数据
const formattedTags = computed(() => {
  const tagList = tagAdminData.value?.tags;
  const detailInfo = detailData.value;
  if (tagList && detailInfo && detailInfo.tag_ids_admin && detailInfo.tag_list) {
    const tags = Object.keys(detailInfo.tag_ids_admin)
      .map(tagId => {
        const tagAdmin = tagList.find((item: { tag_id: number }) => item.tag_id === Number(tagId));

        if (tagAdmin) {
          const childTagIds = Object.keys(detailInfo.tag_ids_admin[tagId]);

          const detailTagList =
            detailInfo.tag_list?.filter(item => childTagIds.includes(String(item.id))) || [];

          // console.log('Filtered detailTagList:', detailTagList)

          return {
            tagAdminName: tagAdmin.tag_name,
            tagAdminId: tagAdmin.tag_id,
            tagList: detailTagList,
          };
        }
        return null;
      })
      .filter(Boolean) as Array<{
      tagAdminName: string;
      tagAdminId: number;
      tagList: { id: number; name: string }[];
    }>;

    return tags;
  }

  return [];
});

// 计算属性：文档头部信息
const docHeader = computed(() => {
  return {
    title: detailData.value?.main_title,
    desc: detailData.value?.sub_title,
    caseLogo: detailData.value?.logo_image_url,
    customerName: detailData.value?.customer_name,
    tags: formattedTags.value,
  };
});

// 为模板提供兼容性支持
const list = computed(() => listData.value || []);

// 页面挂载时滚动到顶部
onMounted(() => {
  const postContainer = document.querySelector('.post-container');
  if (postContainer) {
    postContainer.scrollIntoView({ behavior: 'instant', block: 'start' });
  }
});
</script>

<style lang="scss" scoped>
.post-container {
  @apply flex-col items-center;
  width: 100vw;
  min-width: 1200px;
}
.post-content-wrapper {
  max-width: 1200px;
  width: 100%;
}
.post-article {
  @apply relative flex pb-[160px] justify-between;
  max-width: 1200px;
  width: 100%;
}
</style>
